import {
  IValidationErrors,
  PrimaryButton,
  RiverCheckbox,
  RiverSpinner,
  RiverTextInput,
  useNotification,
  useValidation,
} from "@river/common-ui";
import CloseIcon from "@mui/icons-material/Close";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { PageHeader } from "../../page-header";
import { SubHeader } from "../../sub-header";
import styles from "./customer-contract-details.module.scss";
import { CustomerContext } from "../../customer/customer.context";
import { ContractContext } from "../contract.context";
import { ChangeEvent, useContext, useEffect, useState } from "react";
import { Box, IconButton } from "@mui/material";
import { ContractDto, IContract, ICustomerContact } from "@river/interfaces";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { customerService } from "../../../services";
import dayjs from "dayjs";
import clsx from "clsx";

export const CustomerContractDetails = () => {
  const notify = useNotification();
  const [processing, setProcessing] = useState<boolean>(false);
  const { customer } = useContext(CustomerContext)!;
  const { contract, fetchContract } = useContext(ContractContext)!;
  const validation = useValidation();
  const [validationErrors, setValidationErrors] = useState<IValidationErrors>({
    fields: {},
    list: [],
  });
  const resetValidationErrors = (): void =>
    setValidationErrors({
      fields: {},
      list: [],
    });

  const createEmptyContract = (): IContract => {
    const contract = new ContractDto() as IContract;
    contract.account_managers = [];
    contract.contacts = [];
    contract.auto_renew = false;
    return contract;
  };
  const [contractDetails, setContractDetails] = useState<IContract>(
    contract || createEmptyContract()
  );

  async function saveContractDetails() {
    const errors: IValidationErrors = await validateContractDetails(
      contractDetails!
    );
    if (errors.list.length > 0) {
      return;
    }

    try {
      if (customer?._id) {
        setProcessing(true);
        if (contractDetails?._id) {
          await customerService.updateCustomerContract(
            customer._id,
            contractDetails._id,
            {
              ...contractDetails,
            }
          );
          notify.success({ message: "Contract details updated successfully" });
        } else {
          await customerService.createCustomerContract(customer._id, {
            ...contractDetails!,
          });
          notify.success({ message: "Contract created successfully" });
        }

        await fetchContract();
        resetValidationErrors();
      }
    } catch (message) {
      notify.error({ message });
    } finally {
      setProcessing(false);
    }
  }

  const onPropertyChange = async (
    event: React.ChangeEvent<{ name?: string; value: unknown }>
  ): Promise<void> => {
    const newState: IContract = {
      ...contractDetails!,
      [event.target.name!]: event.target.value,
    };
    Object.setPrototypeOf(newState, Object.getPrototypeOf(contractDetails));
    setContractDetails(newState);
    const newErrors: IValidationErrors = await validation.validateProperty(
      newState,
      event.target.name!,
      validationErrors
    );
    setValidationErrors(newErrors);
  };

  const validateContractDetails = async (
    contractDetails: IContract
  ): Promise<IValidationErrors> => {
    const errors = await validation.validateFields(contractDetails);
    setValidationErrors(errors);
    return errors;
  };

  const onParamPropertyChangeAccountManager = async (
    index: number,
    event: ChangeEvent<{ name: string; value: unknown }>
  ): Promise<void> => {
    const managers: string[] = contractDetails!.account_managers!;
    const newManagers = [...managers];
    //@ts-ignore
    newManagers[index] = event.target.value;
    const newState: IContract = {
      ...contractDetails!,
      account_managers: newManagers,
    };
    Object.setPrototypeOf(newState, Object.getPrototypeOf(contractDetails));
    setContractDetails(newState);
  };

  const onParamPropertyChangeContact = async (
    index: number,
    event: ChangeEvent<{ name: string; value: unknown }>
  ): Promise<void> => {
    const contacts: ICustomerContact[] = contractDetails!.contacts!;
    const newContacts = [...contacts];
    //@ts-ignore
    newContacts[index][event.target.name] = event.target.value;
    const newState: IContract = {
      ...contractDetails!,
      contacts: newContacts,
    };
    Object.setPrototypeOf(newState, Object.getPrototypeOf(contractDetails));
    setContractDetails(newState);
  };

  const renderStartDateField = () => (
    <DatePicker
      label="Start Date"
      value={
        contractDetails?.start_date ? dayjs(contractDetails.start_date) : null
      }
      onChange={(date) => {
        onPropertyChange({
          target: {
            name: "start_date",
            value: date ? date.toDate() : undefined,
          },
        } as ChangeEvent<{ name?: string; value: unknown }>);
      }}
    />
  );

  const renderDurationMonthsField = () => (
    <RiverTextInput
      inputProps={{ type: "number" }}
      label="Duration (Months)"
      value={contractDetails?.duration_months}
      id="duration_months"
      onChangeEvent={(e) => {
        onPropertyChange({
          target: {
            name: "duration_months",
            value: +e.target.value,
          },
        } as ChangeEvent<{ name?: string; value: unknown }>);
      }}
      validationErrors={validationErrors?.fields["duration_months"]}
    />
  );

  const renderRenewalDateField = () => (
    <DatePicker
      label="Renewal Date"
      value={
        contractDetails?.renewal_date
          ? dayjs(contractDetails.renewal_date)
          : null
      }
      onChange={(date) => {
        onPropertyChange({
          target: {
            name: "renewal_date",
            value: date ? date.toDate() : undefined,
          },
        } as ChangeEvent<{ name?: string; value: unknown }>);
      }}
    />
  );

  const renderAutoRenewField = () => (
    <RiverCheckbox
      id="auto_renew"
      label="Auto Renew?"
      checked={!!contractDetails?.auto_renew}
      onChangeEvent={(e) => {
        onPropertyChange({
          target: {
            name: "auto_renew",
            value: e.target.checked,
          },
        } as ChangeEvent<{ name?: string; value: unknown }>);
      }}
    />
  );

  const removeParamAccountManager = (index: number): void => {
    const params: string[] = contractDetails?.account_managers || [];
    if (params.length) {
      const newParams: string[] = [...params];
      newParams.splice(index, 1);
      const newDto: IContract = {
        ...contractDetails!,
        account_managers: newParams,
      };
      Object.setPrototypeOf(newDto, Object.getPrototypeOf(contractDetails));
      setContractDetails(newDto);
    }
  };

  const addParamAccountManager = (): void => {
    const params: string[] = contractDetails?.account_managers || [];
    const newParams: string[] = [...params];
    newParams.push("");
    const newDto: IContract = {
      ...contractDetails!,
      account_managers: newParams,
    };
    Object.setPrototypeOf(newDto, Object.getPrototypeOf(contractDetails));
    setContractDetails(newDto);
  };

  const renderAddParamAccountManagerButton = () => (
    <PrimaryButton
      text="Add Account Manager"
      onClick={addParamAccountManager}
      classes={{ root: styles.addParamButton }}
      variant="text"
      startIcon={<AddCircleIcon />}
    />
  );

  const renderAccountManagerFields = () => (
    <Box className={clsx([styles.addParamsSection, styles.managers])}>
      <Box className={styles.params}>
        <span className={styles.title}>Account Manager(s):</span>
        {renderAddParamAccountManagerButton()}
        <Box className={styles.paramsList}>
          {contractDetails?.account_managers?.map((manager, index) => (
            <Box key={index} className={styles.param}>
              <RiverTextInput
                label="Account Manager"
                id="manager"
                onChangeEvent={(event) =>
                  onParamPropertyChangeAccountManager(index, event)
                }
                className={clsx([styles.field, styles.name])}
                value={manager}
              />
              <IconButton
                classes={{ root: styles.removeParamButton }}
                onClick={() => {
                  removeParamAccountManager(index);
                }}
                size="large"
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );

  const addParamContacts = (): void => {
    const params: ICustomerContact[] = contractDetails?.contacts || [];
    const newParams: ICustomerContact[] = [...params];
    newParams.push({ display_name: "", email: "" });
    const newDto: IContract = {
      ...contractDetails!,
      contacts: newParams,
    };
    Object.setPrototypeOf(newDto, Object.getPrototypeOf(contractDetails));
    setContractDetails(newDto);
  };

  const removeParamContacts = (index: number): void => {
    const params: ICustomerContact[] = contractDetails?.contacts || [];
    if (params.length) {
      const newParams: ICustomerContact[] = [...params];
      newParams.splice(index, 1);
      const newDto: IContract = {
        ...contractDetails!,
        contacts: newParams,
      };
      Object.setPrototypeOf(newDto, Object.getPrototypeOf(contractDetails));
      setContractDetails(newDto);
    }
  };

  const renderAddParamContactsButton = () => (
    <PrimaryButton
      text="Add Contact"
      onClick={addParamContacts}
      classes={{ root: styles.addParamButton }}
      variant="text"
      startIcon={<AddCircleIcon />}
    />
  );

  const renderContactsFields = () => (
    <Box className={clsx([styles.addParamsSection, styles.contacts])}>
      <Box className={styles.params}>
        <span className={styles.title}>Contacts:</span>
        {renderAddParamContactsButton()}
        <Box className={styles.paramsList}>
          {contractDetails?.contacts?.map((contact, index) => (
            <Box key={index} className={styles.param}>
              <RiverTextInput
                label="Display Name"
                id="display_name"
                onChangeEvent={(event) =>
                  onParamPropertyChangeContact(index, event)
                }
                className={clsx([styles.field, styles.name])}
                value={contact.display_name}
              />
              <RiverTextInput
                label="Email"
                id="email"
                onChangeEvent={(event) =>
                  onParamPropertyChangeContact(index, event)
                }
                className={clsx([styles.field, styles.name])}
                value={contact.email}
              />
              <RiverTextInput
                label="Phone"
                id="phone"
                onChangeEvent={(event) =>
                  onParamPropertyChangeContact(index, event)
                }
                className={clsx([styles.field, styles.name])}
                value={contact.phone}
              />
              <RiverTextInput
                label="Position"
                id="position"
                onChangeEvent={(event) =>
                  onParamPropertyChangeContact(index, event)
                }
                className={clsx([styles.field, styles.name])}
                value={contact.position}
              />
              <IconButton
                classes={{ root: styles.removeParamButton }}
                onClick={() => {
                  removeParamContacts(index);
                }}
                size="large"
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );

  useEffect(() => {
    if (contract) {
      setContractDetails(contract);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract]);

  return (
    <>
      <PageHeader title="Contract" />
      <SubHeader subtitle="Details" />
      <Box className={styles.container}>
        <RiverSpinner show={processing} />
        <Box className={styles.fields}>
          {renderStartDateField()}
          {renderDurationMonthsField()}
          {renderRenewalDateField()}
          {renderAutoRenewField()}
          <Box className={styles.lists}>
            {renderAccountManagerFields()}
            {renderContactsFields()}
          </Box>
        </Box>
        <Box className={styles.btnContainer}>
          <PrimaryButton
            text="Save"
            onClick={saveContractDetails}
            disabled={
              processing || !contractDetails || validationErrors.list.length > 0
            }
          />
        </Box>
      </Box>
    </>
  );
};
